import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import ReactMarkdown from 'react-markdown';
import { FaGithub } from "react-icons/fa";
import YouTubePreloader from '../../utils/YouTubePreloader';
import { colors } from '../../utils/theme';
import { createPortal } from 'react-dom';

const fadeIn = keyframes`
  from { 
    opacity: 0;
    backdrop-filter: blur(0px);
  }
  to { 
    opacity: 1;
    backdrop-filter: blur(5px);
  }
`;

const fadeOut = keyframes`
  from { 
    opacity: 1;
    backdrop-filter: blur(5px);
  }
  to { 
    opacity: 0;
    backdrop-filter: blur(0px);
  }
`;

const slideUp = keyframes`
  from { 
    transform: translate(-50%, 20px);
    opacity: 0;
  }
  to { 
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from { 
    transform: translate(-50%, -50%);
    opacity: 1;
  }
  to { 
    transform: translate(-50%, 20px);
    opacity: 0;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  animation: ${({ closing }) => (closing ? fadeOut : fadeIn)} 0.4s cubic-bezier(0.4, 0, 0.2, 1)
    forwards;
`;

const DetailContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #1a1a1a;
  padding: 20px;
  border-radius: 10px;
  z-index: 1001;
  width: 60%;
  max-height: 80vh;
  overflow-y: auto;
  border: 2px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: ${({ closing }) => (closing ? slideDown : slideUp)} 0.5s
    cubic-bezier(0.34, 1.56, 0.64, 1) forwards;

  @media (max-width: 768px) {
    width: 90%;
    max-height: 90vh;
  }

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.4);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${colors.subtitleColor};
  transition: color 0.2s ease;
  padding: 8px;
  line-height: 1;
  
  &:hover {
    color: white;
  }
`;

const LinksContainer = styled.div`
  margin-top: 20px;

  a {
    display: inline-block;
    color: #007acc;
    text-decoration: none;
    font-weight: bold;
    margin: 5px 0;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3vw, 2.2rem);
  color: ${colors.titleColor};
  font-family: "moret-variable", serif;
  font-weight: 400;
  margin: 0;
`;

const Description = styled.p`
  font-size: clamp(1.2rem, 2vw, 1.4rem);
  color: ${colors.subtitleColor};
  font-family: "maitree", serif;
  font-weight: 400;
  margin: 1rem 0;
  line-height: 1.6;
`;

const MarkdownContent = styled.div`
  font-family: "maitree", serif;
  color: ${colors.subtitleColor};
  
  h1 {
    font-size: clamp(1.6rem, 2.5vw, 2rem);
    margin-bottom: 0.5em;
    font-weight: 400;
    font-family: "moret", serif;
  }
  
  h2 {
    font-size: clamp(1.4rem, 2vw, 1.8rem);
    margin-top: 1em;
    margin-bottom: 0.5em;
    font-weight: 400;
    font-family: "moret", serif;
  }
  
  h3 {
    font-size: clamp(1.2rem, 1.5vw, 1.6rem);
    margin-top: 0.8em;
    font-weight: 400;
    font-family: "moret", serif;
  }
  
  p {
    font-size: clamp(1rem, 1.5vw, 1.2rem);
    margin: 0.8em 0;
    line-height: 1.6;
    color: ${colors.subtitleColor};
    font-family: "maitree", serif;
  }
`;

const GitHubLink = styled.a`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 24px;
  padding: 8px 16px;
  border: none;
  background-size: 300% 100%;
  border-radius: 50px;
  font-family: "maitree", sans-serif;
  transition: all 0.4s ease-in-out;
  background-image: linear-gradient(
    to right,
    rgba(114, 114, 114, 0.5),
    rgba(106, 106, 107, 0.5),
    rgba(98, 99, 101, 0.5),
    rgba(89, 92, 94, 0.5),
    rgba(80, 85, 87, 0.5),
    #505557,
    #4a4f51,
    #44494a,
    #3e4344,
    #393c3e,
    #353637,
    #303031,
    #2a2a2a
  );
  box-shadow: 0 4px 15px 0 rgb(242, 242, 242, 0.2);

  &:hover {
    background-position: 100% 0;
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

function ExperimentDetail({ details, onClose }) {
  const [closing, setClosing] = useState(false);

  const handleCloseStart = () => {
    setClosing(true);
    setTimeout(onClose, 500);
  };

  const renderDescription = () => {
    if (Array.isArray(details.description)) {
      return details.description.join('\n');
    }
    return details.description || '';
  };

  const getYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  return createPortal(
    <>
      <Backdrop closing={closing} onClick={handleCloseStart} />
      <DetailContainer closing={closing} onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={handleCloseStart}>&times;</CloseButton>
        <TitleContainer>
          <Title>{details.title}</Title>
          {details.github && (
            <GitHubLink href={details.github} target="_blank" rel="noopener noreferrer">
              <FaGithub />
            </GitHubLink>
          )}
        </TitleContainer>
        {details.info && <Description>{details.info}</Description>}
        <MarkdownContent>
          <ReactMarkdown>{renderDescription()}</ReactMarkdown>
        </MarkdownContent>
        {details.media &&
          details.media.videos &&
          details.media.videos.length > 0 && (
            <div style={{ margin: "20px 0" }}>
              {details.media.videos.map((video, index) => {
                const videoId = getYoutubeId(video);
                return videoId ? (
                  <div key={index} style={{ marginBottom: "20px" }}>
                    <YouTubePreloader
                      videoId={videoId}
                      title={`Video ${index + 1}`}
                    />
                  </div>
                ) : (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      paddingBottom: "56.25%",
                      height: 0,
                      marginBottom: "20px"
                    }}
                  >
                    <iframe
                      title={`Video ${index + 1}`}
                      src={video}
                      frameBorder="0"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        {details.media &&
          details.media.links &&
          details.media.links.length > 0 && (
            <LinksContainer>
              {details.media.links.map((link, index) => (
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  key={index}
                >
                  {link.label}
                </a>
              ))}
            </LinksContainer>
          )}
      </DetailContainer>
    </>,
    document.body
  );
}

export default ExperimentDetail;
